import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { PageSection } from '../../../components/layout/PageSection';
import { useNotificationLogs } from '../../../features/reports/client';
import * as React from 'react';
import { Input } from '../../../components/ui/FormField';
import { usePageTitle } from '../../../lib/hooks.js';
import { DataView } from "../../../components/ui/DataView";

export const Route = createFileRoute('/_auth/logs/notifications')({
  component: NotificationLogPage,
});

interface NotificationLogEntry {
  id: string;
  createdAt?: string;
  sentAt?: string;
  recipientMobile?: string | null;
  recipientEmail?: string | null;
  recipientPhone?: string | null;
  recipientId?: string | null;
  channel: string;
  status: string;
  templateKey: string;
  renderedBody?: string | null;
}

function NotificationLogPage() {
  usePageTitle('Notification Logs');
  const [channel, setChannel] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [date, setDate] = React.useState('');

  const filters: Record<string, string> = {};
  if (channel) filters.channel = channel;
  if (status) filters.status = status;
  if (date) {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    filters.startDate = start.toISOString();
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    filters.endDate = end.toISOString();
  }

  const notificationlogsQuery = useNotificationLogs(Object.keys(filters).length > 0 ? filters : undefined);
  const { data: response } = notificationlogsQuery;
  const dataViewQuery = { ...notificationlogsQuery, data: response?.data };

  return (
    <div>
      <PageHeader title="Notification Logs" className="mb-6" />
      <div className="mb-6 flex gap-4 items-center">
        <select
          value={channel}
          onChange={e => setChannel(e.target.value)}
          className="bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary h-9 px-3"
        >
          <option value="">All Channels</option>
          <option value="SMS">SMS</option>
          <option value="PUSH">Push</option>
          <option value="EMAIL">Email</option>
        </select>
        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          className="bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary h-9 px-3"
        >
          <option value="">All Statuses</option>
          <option value="DELIVERED">Delivered</option>
          <option value="SENT">Sent</option>
          <option value="QUEUED">Queued</option>
          <option value="FAILED">Failed</option>
          <option value="UNDELIVERED">Undelivered</option>
        </select>
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-auto" />
      </div>
      <DataView query={dataViewQuery}>
        {(data) => (
          <PageSection>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border-border text-left whitespace-nowrap">
                <thead className="bg-surface-2">
                  <tr>
                    <th className="border px-4 py-2">Timestamp</th>
                    <th className="border px-4 py-2">Recipient</th>
                    <th className="border px-4 py-2">Channel</th>
                    <th className="border px-4 py-2">Status</th>
                    <th className="border px-4 py-2">Template/Title</th>
                  </tr>
                </thead>
                <tbody>
                  {(data as NotificationLogEntry[])?.map((log) => {
                    const rawDate = log.createdAt || log.sentAt;
                    const recipient = log.recipientMobile || log.recipientEmail || log.recipientPhone || log.recipientId || '—';
                    return (
                      <tr key={log.id}>
                        <td className="border px-4 py-2 font-mono text-xs">
                          {rawDate ? new Date(rawDate).toLocaleString() : '—'}
                        </td>
                        <td className="border px-4 py-2 text-sm">{recipient}</td>
                        <td className="border px-4 py-2 text-sm">{log.channel}</td>
                        <td className="border px-4 py-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            log.status === 'DELIVERED' || log.status === 'SENT' ? 'bg-success-bg text-success-text' :
                            log.status === 'FAILED' || log.status === 'UNDELIVERED' ? 'bg-danger-bg text-danger-text' :
                            'bg-warning-bg text-warning-text'
                          }`}>
                            {log.status}
                          </span>
                        </td>
                        <td className="border px-4 py-2 text-sm font-medium">{log.templateKey}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </PageSection>
        )}
      </DataView>
    </div>
  );
}

